import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import {
  Phase2,
  AgFinancingCollateralAnalysisStatus,
  AgFinancingInput,
  AgFinancingPhase2Status
} from '@indigo-ag/schema';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';

import { SingleRow } from '../../shared/SingleRow';
import {
  PhaseSection,
  CollateralUtil,
  WithConditionalTooltip,
  tooltipHelperForDisabledElements,
  FieldMonitoringUtil,
  Phase2Util
} from 'shared';
import { CollateralAnalysisState } from '../../features/collateral-analysis/sections/CollateralAnalysisState/CollateralAnalysisState';
import { StartAnalysisModal } from '../../phases/phase2/StartAnalysisModal/StartAnalysisModal';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { StartAnalysisPhase2Disclaimer } from '../../shared/StartAnalysisPhase2Disclaimer/StartAnalysisPhase2Disclaimer';
import { UserContext } from 'core/components/GateWay/GateWay';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { ToggleOnHoldCollateralAnalysis } from '../../shared/ToggleOnHoldCollateralAnalysis/ToggleOnHoldCollateralAnalysis';

interface CollateralSectionProps {
  collateralInfo: Phase2['collateral'];
  fieldMonitoringInfo: Phase2['fieldMonitoring'];
  matriculas: Phase2['matriculas'];
}

export const CollateralSection: React.FunctionComponent<CollateralSectionProps> = ({
  collateralInfo,
  matriculas,
  fieldMonitoringInfo
}) => {
  /**
   * State
   */
  const [openStartAnalysis, setOpenStartAnalysis] = useState(false);
  const [isRestartingAnalysis, setIsRestartingAnalysis] = useState(false);

  /**
   * Finance Process Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  /**
   * User Context
   */
  const { isSuper } = useContext(UserContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const handleCloseStartAnalysis = () => {
    setOpenStartAnalysis(false);
  };

  const { addFlashMessage } = useFlashMessage();

  const restartCollateralAnalysis = () => {
    setIsRestartingAnalysis(true);

    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinance?.id,
      phase2: {
        status:
          agFinance?.phase2?.status === AgFinancingPhase2Status.DENIED ||
          agFinance?.phase2?.status === AgFinancingPhase2Status.PENDING_RESTART
            ? AgFinancingPhase2Status.IN_PROGRESS
            : undefined,
        collateral: {
          status: AgFinancingCollateralAnalysisStatus.STARTED
        }
      }
    };

    upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    })
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('collateralAnalysis')} ${i18next.t('restarted')}`,
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsRestartingAnalysis(false);
      });
  };

  const isOnHold = agFinance?.phase2?.status === AgFinancingPhase2Status.ON_HOLD;
  const canStartCollateral = CollateralUtil.canStart(
    agFinance?.canStartPhase2,
    agFinance?.collateralType,
    agFinance?.phase2
  );

  const hasPromissoryNote = Phase2Util.hasPromissoryNote(agFinance);
  const canViewStartButton =
    agFinance?.phase2?.status !== AgFinancingPhase2Status.AWAITING_COMMERCIAL_DEAL &&
    (hasPromissoryNote || FieldMonitoringUtil.hasBeenSent(fieldMonitoringInfo?.status));

  const renderStartButton = () => {
    return !CollateralUtil.hasStarted(collateralInfo) && canViewStartButton ? (
      <>
        <WithConditionalTooltip visible={!canStartCollateral} title={<StartAnalysisPhase2Disclaimer />} placement="top">
          <Button
            variant="primary"
            size="small"
            onClick={() => setOpenStartAnalysis(true)}
            disabled={(!isSuper && !canStartCollateral) || isOnHold}
            style={tooltipHelperForDisabledElements(!isSuper && !canStartCollateral)}
          >
            {i18next.t('start')}
          </Button>
        </WithConditionalTooltip>
        <StartAnalysisModal
          open={openStartAnalysis}
          startExternalProcess={{ flc: false, luchesi: true, terramagna: false }}
          closeModal={handleCloseStartAnalysis}
        />
      </>
    ) : (
      undefined
    );
  };

  const renderReopenButton = () => {
    return CollateralUtil.canReopen(agFinance?.phase2) ? (
      <Button
        variant="primary"
        size="small"
        className="text-transform-capitalize"
        onClick={() => restartCollateralAnalysis()}
        busy={isRestartingAnalysis}
      >
        {i18next.t('reopen')}
      </Button>
    ) : (
      undefined
    );
  };

  return (
    <>
      <PhaseSection
        title={i18next.t('collateralData')}
        topRightContent={
          <>
            {renderStartButton()}
            {renderReopenButton()}
            <ToggleOnHoldCollateralAnalysis />
          </>
        }
      >
        {collateralInfo?.startedAt ? (
          <CollateralAnalysisState collateralInfo={collateralInfo} matriculas={matriculas} />
        ) : (
          <SingleRow title={i18next.t('status')} content={i18next.t('notStarted')} />
        )}
      </PhaseSection>
    </>
  );
};
